import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Button,
  TextField,
  MenuItem,
  Chip,
  Tooltip,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TimelineIcon from '@mui/icons-material/Timeline';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useAuth } from '../context/AuthContext';

interface VIXReading {
  id: number;
  reading_date: string;
  reading_time: string;
  vix_value: number;
  vix_open: number | null;
  vix_high: number | null;
  vix_low: number | null;
  vix_prev_close: number | null;
  vix_change: number | null;
  vix_change_pct: number | null;
  source: string;
}

interface DailySummary {
  summary_date: string;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  prev_close: number | null;
  change: number | null;
  change_pct: number | null;
  readings_count: number;
}

const VIX_LEVELS = [
  { threshold: 30, label: 'Extreme Fear', color: '#f44336' },
  { threshold: 25, label: 'High Fear', color: '#ff5722' },
  { threshold: 20, label: 'Elevated', color: '#ff9800' },
  { threshold: 15, label: 'Normal', color: '#4caf50' },
  { threshold: 0, label: 'Low Vol', color: '#2196f3' },
];

function getVIXLevel(value: number) {
  for (const level of VIX_LEVELS) {
    if (value >= level.threshold) return level;
  }
  return VIX_LEVELS[VIX_LEVELS.length - 1];
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0];
}

const pctColor = (v: number | null) =>
  v === null ? '#999' : v > 0 ? '#f44336' : v < 0 ? '#4caf50' : '#999';

const VIXView: React.FC = () => {
  const { token } = useAuth();

  // View mode
  const [viewMode, setViewMode] = useState<'intraday' | 'history'>('intraday');

  // Intraday state
  const [readings, setReadings] = useState<VIXReading[]>([]);
  const [latest, setLatest] = useState<VIXReading | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchingNow, setFetchingNow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [availableDates, setAvailableDates] = useState<string[]>([]);

  // History state
  const thirtyDaysAgo = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDate(d);
  }, []);
  const [historyStart, setHistoryStart] = useState(thirtyDaysAgo);
  const [historyEnd, setHistoryEnd] = useState(formatDate(new Date()));
  const [dailySummaries, setDailySummaries] = useState<DailySummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [historySortCol, setHistorySortCol] = useState<string>('summary_date');
  const [historySortDir, setHistorySortDir] = useState<'asc' | 'desc'>('desc');

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  // ---------- Intraday fetching ----------
  const fetchDates = useCallback(async () => {
    try {
      const res = await fetch('/api/vix/dates', { headers: headers() });
      if (res.ok) {
        const data = await res.json();
        setAvailableDates(data.dates || []);
      }
    } catch {}
  }, [headers]);

  const fetchReadings = useCallback(async (date: string) => {
    setLoading(true);
    setError(null);
    try {
      const url = date === formatDate(new Date())
        ? '/api/vix/today'
        : `/api/vix/date/${date}`;
      const res = await fetch(url, { headers: headers() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setReadings(data.readings || []);
      if (data.latest) setLatest(data.latest);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    if (viewMode === 'intraday') {
      fetchDates();
      fetchReadings(selectedDate);
    }
  }, [selectedDate, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const interval = setInterval(() => {
      if (viewMode === 'intraday' && selectedDate === formatDate(new Date())) {
        fetchReadings(selectedDate);
      }
    }, 60_000);
    return () => clearInterval(interval);
  }, [selectedDate, viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- History fetching ----------
  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await fetch(
        `/api/vix/history?start_date=${historyStart}&end_date=${historyEnd}`,
        { headers: headers() }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setDailySummaries(data.summaries || []);
    } catch (e: any) {
      setHistoryError(e.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [headers, historyStart, historyEnd]);

  useEffect(() => {
    if (viewMode === 'history') {
      fetchHistory();
    }
  }, [viewMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerFetch = async () => {
    setFetchingNow(true);
    try {
      const res = await fetch('/api/vix/fetch', { method: 'POST', headers: headers() });
      if (res.ok) {
        await fetchReadings(selectedDate);
        await fetchDates();
      }
    } finally {
      setFetchingNow(false);
    }
  };

  // ---------- Intraday derived ----------
  const chartData = readings.map((r) => ({
    time: r.reading_time,
    vix: r.vix_value,
  }));

  const currentValue = latest?.vix_value ?? readings[readings.length - 1]?.vix_value;
  const prevClose = latest?.vix_prev_close ?? readings[0]?.vix_prev_close;
  const openVal = latest?.vix_open ?? readings[0]?.vix_open;
  const highVal = latest?.vix_high ?? readings[0]?.vix_high;
  const lowVal = latest?.vix_low ?? readings[0]?.vix_low;
  const change = latest?.vix_change;
  const changePct = latest?.vix_change_pct;

  const level = currentValue ? getVIXLevel(currentValue) : null;
  const isUp = change !== null && change !== undefined && change > 0;
  const minVix = chartData.length ? Math.floor(Math.min(...chartData.map((d) => d.vix)) - 0.5) : 10;
  const maxVix = chartData.length ? Math.ceil(Math.max(...chartData.map((d) => d.vix)) + 0.5) : 30;

  // ---------- History derived ----------
  const historyChartData = useMemo(() => {
    return [...dailySummaries]
      .sort((a, b) => a.summary_date.localeCompare(b.summary_date))
      .map((s) => ({
        date: s.summary_date,
        close: s.close,
        high: s.high,
        low: s.low,
        open: s.open,
      }));
  }, [dailySummaries]);

  const sortedSummaries = useMemo(() => {
    const arr = [...dailySummaries];
    arr.sort((a: any, b: any) => {
      const aVal = a[historySortCol];
      const bVal = b[historySortCol];
      if (aVal === null) return 1;
      if (bVal === null) return -1;
      if (typeof aVal === 'string') {
        return historySortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return historySortDir === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return arr;
  }, [dailySummaries, historySortCol, historySortDir]);

  const historyStats = useMemo(() => {
    if (!dailySummaries.length) return null;
    const closes = dailySummaries.filter((s) => s.close !== null).map((s) => s.close!);
    const changes = dailySummaries.filter((s) => s.change_pct !== null).map((s) => s.change_pct!);
    return {
      avgClose: closes.length ? closes.reduce((a, b) => a + b, 0) / closes.length : 0,
      maxClose: closes.length ? Math.max(...closes) : 0,
      minClose: closes.length ? Math.min(...closes) : 0,
      avgChange: changes.length ? changes.reduce((a, b) => a + b, 0) / changes.length : 0,
      daysUp: changes.filter((c) => c > 0).length,
      daysDown: changes.filter((c) => c < 0).length,
      tradingDays: dailySummaries.length,
    };
  }, [dailySummaries]);

  const histMinVix = historyChartData.length
    ? Math.floor(Math.min(...historyChartData.map((d) => d.low ?? d.close ?? 10)) - 1)
    : 10;
  const histMaxVix = historyChartData.length
    ? Math.ceil(Math.max(...historyChartData.map((d) => d.high ?? d.close ?? 30)) + 1)
    : 30;

  const handleHistorySort = (col: string) => {
    setHistorySortDir(historySortCol === col && historySortDir === 'desc' ? 'asc' : 'desc');
    setHistorySortCol(col);
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShowChartIcon fontSize="large" color="primary" />
          VIX Index Tracker
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => v && setViewMode(v)}
          size="small"
        >
          <ToggleButton value="intraday">
            <TimelineIcon sx={{ mr: 0.5 }} /> Intraday
          </ToggleButton>
          <ToggleButton value="history">
            <CalendarMonthIcon sx={{ mr: 0.5 }} /> History
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* ==================== INTRADAY VIEW ==================== */}
      {viewMode === 'intraday' && (
        <>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              select
              size="small"
              label="Date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value={formatDate(new Date())}>Today</MenuItem>
              {availableDates
                .filter((d) => d !== formatDate(new Date()))
                .map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
            </TextField>
            <Tooltip title="Fetch VIX now">
              <span>
                <IconButton onClick={triggerFetch} disabled={fetchingNow} color="primary">
                  {fetchingNow ? <CircularProgress size={24} /> : <RefreshIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={60} />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading VIX data...</Typography>
            </Box>
          ) : (
            <>
              {/* Summary cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ borderRadius: 3, borderTop: `4px solid ${level?.color || '#999'}`, height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Current VIX</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="h3" sx={{ fontWeight: 'bold', color: level?.color }}>
                          {currentValue?.toFixed(2) || '--'}
                        </Typography>
                        {level && (
                          <Chip label={level.label} size="small" sx={{ bgcolor: level.color, color: '#fff', fontWeight: 'bold' }} />
                        )}
                      </Box>
                      {change !== null && change !== undefined && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                          {isUp ? <TrendingUpIcon sx={{ color: '#f44336', fontSize: 18 }} /> : <TrendingDownIcon sx={{ color: '#4caf50', fontSize: 18 }} />}
                          <Typography variant="body2" sx={{ color: isUp ? '#f44336' : '#4caf50', fontWeight: 'bold' }}>
                            {change > 0 ? '+' : ''}{change.toFixed(2)} ({changePct !== null && changePct !== undefined ? `${changePct > 0 ? '+' : ''}${changePct.toFixed(2)}%` : ''})
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Open</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{openVal?.toFixed(2) || '--'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Prev Close</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{prevClose?.toFixed(2) || '--'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2.5}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Day High</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f44336' }}>{highVal?.toFixed(2) || '--'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3} md={2.5}>
                  <Card sx={{ borderRadius: 3, height: '100%' }}>
                    <CardContent>
                      <Typography variant="body2" color="text.secondary">Day Low</Typography>
                      <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>{lowVal?.toFixed(2) || '--'}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Intraday chart */}
              <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Intraday VIX — {selectedDate}
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({readings.length} readings, every 15 min)
                  </Typography>
                </Typography>
                {chartData.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography color="text.secondary">
                      No VIX data for this date.{' '}
                      {selectedDate === formatDate(new Date()) && 'Data appears after 8:00 AM ET.'}
                    </Typography>
                    {selectedDate === formatDate(new Date()) && (
                      <Button variant="outlined" sx={{ mt: 2 }} onClick={triggerFetch} disabled={fetchingNow}>
                        Fetch Now
                      </Button>
                    )}
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={420}>
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="vixGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={level?.color || '#ff9800'} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={level?.color || '#ff9800'} stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                      <YAxis domain={[minVix, maxVix]} tick={{ fontSize: 11 }} />
                      <RechartsTooltip
                        contentStyle={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: 8 }}
                        formatter={(value: number) => [value.toFixed(2), 'VIX']}
                        labelFormatter={(label: string) => `${label} ET`}
                      />
                      {prevClose && (
                        <ReferenceLine
                          y={prevClose}
                          stroke="#666"
                          strokeDasharray="5 5"
                          label={{ value: `Prev ${prevClose.toFixed(2)}`, position: 'right', fill: '#888', fontSize: 11 }}
                        />
                      )}
                      <ReferenceLine y={20} stroke="#ff980044" strokeDasharray="3 3" />
                      <ReferenceLine y={30} stroke="#f4433644" strokeDasharray="3 3" />
                      <Area
                        type="monotone"
                        dataKey="vix"
                        stroke={level?.color || '#ff9800'}
                        fill="url(#vixGradient)"
                        strokeWidth={2.5}
                        dot={false}
                        activeDot={{ r: 5, fill: level?.color || '#ff9800' }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </Paper>

              {/* VIX level guide */}
              <Paper sx={{ p: 2, borderRadius: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>VIX Level Guide</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  {VIX_LEVELS.map((lv) => (
                    <Chip
                      key={lv.label}
                      label={`${lv.threshold}+ ${lv.label}`}
                      sx={{
                        bgcolor: currentValue && currentValue >= lv.threshold && (VIX_LEVELS.indexOf(lv) === 0 || currentValue < VIX_LEVELS[VIX_LEVELS.indexOf(lv) - 1].threshold) ? lv.color : 'transparent',
                        color: currentValue && currentValue >= lv.threshold && (VIX_LEVELS.indexOf(lv) === 0 || currentValue < VIX_LEVELS[VIX_LEVELS.indexOf(lv) - 1].threshold) ? '#fff' : lv.color,
                        border: `1px solid ${lv.color}`,
                        fontWeight: 'bold',
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            </>
          )}
        </>
      )}

      {/* ==================== HISTORY VIEW ==================== */}
      {viewMode === 'history' && (
        <>
          {/* Date range picker */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={historyStart}
                  onChange={(e) => setHistoryStart(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <TextField
                  label="End Date"
                  type="date"
                  value={historyEnd}
                  onChange={(e) => setHistoryEnd(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <Button
                  variant="contained"
                  onClick={fetchHistory}
                  disabled={historyLoading}
                  startIcon={historyLoading ? <CircularProgress size={20} color="inherit" /> : <CalendarMonthIcon />}
                  sx={{ borderRadius: 2, fontWeight: 'bold' }}
                >
                  {historyLoading ? 'Loading...' : 'Load History'}
                </Button>
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {[7, 30, 60, 90].map((days) => (
                    <Chip
                      key={days}
                      label={`${days}d`}
                      size="small"
                      clickable
                      variant="outlined"
                      onClick={() => {
                        const d = new Date();
                        d.setDate(d.getDate() - days);
                        setHistoryStart(formatDate(d));
                        setHistoryEnd(formatDate(new Date()));
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {historyError && <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert>}

          {historyLoading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={60} />
              <Typography sx={{ mt: 2, color: 'text.secondary' }}>Loading VIX history...</Typography>
            </Box>
          ) : dailySummaries.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No VIX history data for this date range. Data accumulates as the scheduler runs each trading day.
            </Alert>
          ) : (
            <>
              {/* Stats cards */}
              {historyStats && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Avg Close</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {historyStats.avgClose.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Period High</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f44336' }}>
                          {historyStats.maxClose.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Period Low</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                          {historyStats.minClose.toFixed(2)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Avg Chg %</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: pctColor(historyStats.avgChange) }}>
                          {historyStats.avgChange > 0 ? '+' : ''}{historyStats.avgChange.toFixed(2)}%
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Days Up / Down</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          <span style={{ color: '#f44336' }}>{historyStats.daysUp}</span>
                          {' / '}
                          <span style={{ color: '#4caf50' }}>{historyStats.daysDown}</span>
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ borderRadius: 3, height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Trading Days</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                          {historyStats.tradingDays}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Daily close chart */}
              <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  VIX Daily Close — {historyStart} to {historyEnd}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={historyChartData}>
                    <defs>
                      <linearGradient id="histGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#ff9800" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#ff9800" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis domain={[histMinVix, histMaxVix]} tick={{ fontSize: 11 }} />
                    <RechartsTooltip
                      contentStyle={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: 8 }}
                      formatter={(value: number, name: string) => [
                        value?.toFixed(2),
                        name.charAt(0).toUpperCase() + name.slice(1),
                      ]}
                    />
                    <ReferenceLine y={20} stroke="#ff980044" strokeDasharray="3 3" />
                    <ReferenceLine y={30} stroke="#f4433644" strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="close" stroke="#ff9800" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 6 }} />
                    <Line type="monotone" dataKey="high" stroke="#f4433666" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                    <Line type="monotone" dataKey="low" stroke="#4caf5066" strokeWidth={1} dot={false} strokeDasharray="3 3" />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>

              {/* Daily summary table */}
              <Paper sx={{ borderRadius: 3, mb: 3 }}>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {[
                          { key: 'summary_date', label: 'Date' },
                          { key: 'open', label: 'Open' },
                          { key: 'high', label: 'High' },
                          { key: 'low', label: 'Low' },
                          { key: 'close', label: 'Close' },
                          { key: 'change', label: 'Change' },
                          { key: 'change_pct', label: 'Change %' },
                          { key: 'readings_count', label: 'Readings' },
                        ].map((col) => (
                          <TableCell key={col.key} sx={{ fontWeight: 'bold' }} align={col.key === 'summary_date' ? 'left' : 'right'}>
                            <TableSortLabel
                              active={historySortCol === col.key}
                              direction={historySortCol === col.key ? historySortDir : 'desc'}
                              onClick={() => handleHistorySort(col.key)}
                            >
                              {col.label}
                            </TableSortLabel>
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedSummaries.map((s) => {
                        const lvl = s.close ? getVIXLevel(s.close) : null;
                        return (
                          <TableRow
                            key={s.summary_date}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                              setSelectedDate(s.summary_date);
                              setViewMode('intraday');
                            }}
                          >
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              {s.summary_date}
                              {lvl && (
                                <Chip
                                  label={lvl.label}
                                  size="small"
                                  sx={{ ml: 1, height: 18, fontSize: 10, bgcolor: lvl.color, color: '#fff' }}
                                />
                              )}
                            </TableCell>
                            <TableCell align="right">{s.open?.toFixed(2) ?? '--'}</TableCell>
                            <TableCell align="right" sx={{ color: '#f44336' }}>{s.high?.toFixed(2) ?? '--'}</TableCell>
                            <TableCell align="right" sx={{ color: '#4caf50' }}>{s.low?.toFixed(2) ?? '--'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{s.close?.toFixed(2) ?? '--'}</TableCell>
                            <TableCell align="right" sx={{ color: pctColor(s.change), fontWeight: 'bold' }}>
                              {s.change !== null ? `${s.change > 0 ? '+' : ''}${s.change.toFixed(2)}` : '--'}
                            </TableCell>
                            <TableCell align="right" sx={{ color: pctColor(s.change_pct), fontWeight: 'bold' }}>
                              {s.change_pct !== null ? `${s.change_pct > 0 ? '+' : ''}${s.change_pct.toFixed(2)}%` : '--'}
                            </TableCell>
                            <TableCell align="right">{s.readings_count}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default VIXView;
